import { api } from "@/lib/api/api";
import { convertTZ } from "@/lib/utils";
import { isValidEmail } from "@/lib/validation/email";
import {
  ApiClientError,
  AuthenticatedUser,
  IResource,
  ISchedule,
  Id,
  Office,
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED,
  PRESENCE_TYPE_IN_PERSON,
  PRESENCE_TYPE_REMOTE,
  Period,
  Resource,
  ScheduleRequest,
  ScheduleRequestUpdate,
  ScheduleSlotRequestUpdate,
  ScheduleVisibility,
  isParking,
  parseDateOnlyString
} from "@gettactic/api";
import { pad } from "@gettactic/helpers/src/numbers/pad";
import { buildRoomReservationSlots } from "@/features/reserve/utils/buildRoomReservationSlots";
import { getEditedReservationRoomIds } from "@/features/reserve/utils/getEditedReservationRoomIds";
import {
  getRoomsOverCapacityMessage,
  reservationErrorMessagesByCode
} from "@/features/reserve/utils/reservationErrorMessages";
import { resolveOnlineMeetingUpdate } from "@/features/reserve/utils/resolveOnlineMeetingUpdate";
import { addDays, addMinutes, format, parseISO } from "date-fns";
import pluralize from "pluralize";
import { rrulestr, RRule } from "rrule";

export { findCheckinTargetSlot } from "@gettactic/helpers";

/**
 * Text for label & description interpolation
 */
export const RESERVE_TEXTS = {
  days: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
  ],
  hoursPlural: "hours",
  hoursSingular: "hour",
  minutesPlural: "minutes",
  minutesSingular: "minute",
  title: {
    meeting_room: "Room Reservation",
    workspace: "Desk Reservation"
  },
  selectResource: {
    meeting_room: "Select a room",
    workspace: "Select a desk",
    parking: "Select a parking space"
  },
  overCapacityTitle: {
    meeting_room: "Room Capacity Reached",
    workspace: "Desk Capacity Reached",
    parking: "Parking Capacity Reached",
    locker: "Locker Capacity Reached"
  },
  overCapacityTotalSlots: {
    meeting_room: "Room Capacity",
    workspace: "Desk Capacity",
    parking: "Parking Capacity",
    locker: "Locker Capacity"
  },
  overCapacityUsersLength: {
    meeting_room: "Invited",
    workspace: "Invited",
    parking: "Invited",
    locker: "Invited"
  },
  selectParking: "Select parking (optional)",
  noParkingSpace: "No Parking Space",
  reserve: {
    meeting_room: "Reserve Room",
    workspace: "Reserve Desk",
    parking: "Reserve Parking",
    locker: "Reserve Locker"
  },
  selectDate: "Select date",
  selectTime: "Select starting time",
  selectTimeTitle: "Time",
  selectDuration: "Select duration",
  selectTeams: "Select teams",
  selectStart: "Select",
  addTitle: "Add title",
  addDescription: "Add agenda or notes",
  approvalPresenceOptions: {
    [PRESENCE_STATUS_REJECTED]: "No",
    [PRESENCE_STATUS_ACCEPTED]: "Yes",
    [PRESENCE_STATUS_PENDING]: "Maybe",
    [PRESENCE_TYPE_IN_PERSON]: (resource: string) => `Yes, in ${resource}`,
    [PRESENCE_TYPE_REMOTE]: "Yes, attending virtually"
  },
  RespondPresenceCount: {
    [PRESENCE_STATUS_PENDING]: (count: number) => `Pending ${count}`,
    [PRESENCE_STATUS_ACCEPTED]: (count: number) => `Yes ${count}`,
    [PRESENCE_STATUS_REJECTED]: (count: number) => `No ${count}`
  }
};

export const noParkingResource: Resource = {
  id: "none",
  type: "parking_lot",
  parent_id: null,
  label: RESERVE_TEXTS.noParkingSpace,
  reserved_slots: 0,
  available_slots: 0,
  is_approvable: false,
  total_slots: 0,
  maximum_slots_per_user: 0,
  is_available: false,
  is_open: false,
  has_tags: false,
  is_shareable: false,
  map_url: "",
  name: RESERVE_TEXTS.noParkingSpace,
  description: "",
  office_id: "",
  teams: null,
  users: null,
  weight: 0,
  media_variants: [],
  visible_assignment: null,
  user_visible_assignments: [],
  team_visible_assignments: []
};

export interface DurationOption {
  label: string;
  value: number;
  durationLabel: string;
  time: Date;
}

export function getDurationLabel(durationMinutes: number, short = false) {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  if (!short) {
    return `${pad(hours)} ${pluralize("Hour", hours)} ${pad(
      minutes
    )} ${pluralize("Minute", minutes)}`;
  }
  return `${pad(hours)}h ${pad(minutes)}m`;
}

export function getDurationOptionFromTime(startTime: Date, filterTime: string) {
  const filterDate = new Date(startTime);
  const filterTimeParts = filterTime.split(":");
  filterDate.setHours(
    Number(filterTimeParts[0]) + (filterTime.includes("pm") ? 12 : 0),
    Number(filterTimeParts[1].replace("am", "").replace("pm", ""))
  );

  if (filterDate <= startTime) {
    // This is future, add a day
    filterDate.setDate(filterDate.getDate() + 1);
  }

  const minutesDifference = Math.floor(
    (filterDate.getTime() - startTime.getTime()) / 60000
  );

  return {
    label: filterTime,
    value: minutesDifference,
    durationLabel: getDurationLabel(minutesDifference, true),
    time: filterDate
  };
}

export function getDurationOptionFromMinutes(
  startTime: Date,
  minutes: number
): DurationOption {
  const endTime = new Date(startTime.getTime() + minutes * 60 * 1000);
  const endTimeLabel = format(endTime, "h:mmaaa");
  return {
    value: minutes,
    label: endTimeLabel,
    durationLabel: getDurationLabel(minutes, true),
    time: endTime
  };
}

export function getDurationOptions(startTime: Date): DurationOption[] {
  const limitDate = addDays(startTime, 1);
  const durationOptions: DurationOption[] = [];
  for (
    let durationMinutes = 15, hoursLimit = 24, minutesIncrement = 15;
    durationMinutes <= hoursLimit * 60;
    durationMinutes += minutesIncrement
  ) {
    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
    if (endTime >= limitDate) {
      // we don't want to go beyond limit time. So we use as last item the time remaining between
      // start time and limit time, so we recalculate durationMinutes
      durationMinutes = Math.floor(
        (limitDate.getTime() - startTime.getTime()) / 1000 / 60
      );
    }
    const durationLabel = getDurationLabel(durationMinutes, true);
    const endTimeLabel = format(endTime, "h:mmaaa");
    durationOptions.push({
      value: durationMinutes,
      label: endTimeLabel,
      durationLabel,
      time: endTime
    });
    // we break the loop because we mutated durationMinutes
    if (endTime >= limitDate) {
      break;
    }
  }

  return durationOptions;
}

export type SaveReservationDataUsers = {
  value: string;
}[];

export type OnlineMeetingProvider = "teams_for_business" | "hangouts_meet";

export type SaveReservationData = {
  title?: string;
  time: Date;
  duration: { value: number; label?: string; durationLabel?: string };
  weekDates: Date[];
  resource: IResource;
  parking_resource: IResource | null;
  users: SaveReservationDataUsers;
  teams: { value: string }[];
  organizer: { value: string };
  description?: string;
  visibility?: ScheduleVisibility;
  recurring: string;
  online_meeting?: {
    provider: OnlineMeetingProvider | null;
  } | null;
  customMeetingLink?: string;
};

export function validateReservationsData(data: SaveReservationData) {
  const throwDataError = (msg: string) => {
    throw new Error(
      `Invalid SaveReservationData - Failed validation: [${msg}] - Data: ${JSON.stringify(
        data
      )}`
    );
  };
  if (!data.resource) {
    throwDataError("resource");
  }
  if (!data.duration) {
    throwDataError("duration");
  }
  if (data.duration.value <= 0) {
    throwDataError("duration.value <= 0");
  }
  if (data.weekDates.length < 1) {
    throwDataError("weekDates.length < 1");
  }
  if (!data.time) {
    throwDataError("time");
  }
}

export async function editReservations(
  original: ISchedule,
  data: SaveReservationData,
  editRecurring: boolean,
  dateFormat: string,
  orgSlug: string
) {
  // If we are editing a meeting room, then multiple users (attendees) can be included
  // therefore we separate the logic for single-user reservations from multi-user resources.
  // Specifically for a single-user reservation, we only want to update the primary slot ID
  // Where we will need to include the array of slots for meeting rooms.
  const isMeetingRoom = original.resource.type === "meeting_room";
  const slot = !isMeetingRoom
    ? original.slots.find((x) => !isParking(x.resource))
    : null;
  const parkingSlot = !isMeetingRoom
    ? original.slots.find((x) => isParking(x.resource))
    : null;

  // Pull out the resource information from the form data
  const { resource } = data;

  // Create a list of users from the form data. These should already be cross referenced
  // against existing users in recurring reservation templates & against speficic reservation instances in recurring series.
  const users = [...new Set(data.users.map((x) => x.value))];
  if (!resource) {
    throw new Error("Resource is required");
  }

  // If for some reason the organizer is not included in the list of values,
  // manually add them to the list of users.
  if (isMeetingRoom) {
    if (data.organizer?.value) {
      if (!users.find((x) => x === data.organizer?.value)) {
        users.push(data.organizer.value);
      }
    }
  }

  // The form edits a single resource; a room reservation can hold several.
  const editedRoomIds = isMeetingRoom
    ? getEditedReservationRoomIds(original, resource.id)
    : [resource.id];

  // Format the start, end and time zone for schedule slots
  const slots: ScheduleSlotRequestUpdate[] = [];
  let dayPart = "";
  let startTimeForRecurring = "";
  let endTimeForRecurring = "";
  data.weekDates.forEach((startDate) => {
    startDate.setHours(data.time.getHours());
    startDate.setMinutes(data.time.getMinutes());
    startDate.setSeconds(0);
    dayPart = format(startDate, "yyyy-MM-dd");
    const endDate = addMinutes(startDate, data.duration.value);
    const formattedStartDate = `${dayPart}T${format(startDate, "HH:mm:ss")}.000Z`;
    const period: Period = {
      start: formattedStartDate,
      end: `${format(endDate, "yyyy-MM-dd")}T${format(endDate, "HH:mm:ss")}.000Z`,
      time_zone: ""
    };

    if (!startTimeForRecurring) {
      endTimeForRecurring = `${format(endDate, "HH:mm:ss")}.000Z`;
      startTimeForRecurring = `${format(startDate, "HH:mm:ss")}.000Z`;
    }

    // For each user in the array, we create an object that will be included in the schedule slots
    users.forEach((user) => {
      // One slot per room per user: the update replaces the whole slot set, so the rooms
      // the form is not editing have to travel along or they are dropped.
      editedRoomIds.forEach((roomId) => {
        const req: ScheduleSlotRequestUpdate = {
          resource_id: roomId,
          start: period.start,
          end: period.end,
          time_zone: period.time_zone,
          ...getUserSlotIdentifier(user),
          slots: 1
        };

        // This is for reservations that are NOT meeting rooms and are not parking resources.
        if (!isMeetingRoom && slot) {
          req.id = slot.id;
        }

        // If we have existing slots that match the same user ID's provided in the form data,
        // then we should include those slots ID's in the API request. If they are not included,
        // then it's assumed that the user has been removed from the reservation.
        if (isMeetingRoom) {
          req.id = original.slots.find(
            (x) => x.user.id === user && x.resource.id === roomId
          )?.id;
        }

        slots.push(req);
      });

      // If the form data parking resource is not null, we need to edit the parking resource
      if (
        data.parking_resource &&
        data.parking_resource.id !== noParkingResource.id
      ) {
        const editedParkingSlot: ScheduleSlotRequestUpdate = {
          resource_id: data.parking_resource.id,
          ...getUserSlotIdentifier(user),
          team_id: null,
          slots: 1,
          start: period.start,
          end: period.end,
          time_zone: period.time_zone
        };
        if (parkingSlot) {
          editedParkingSlot.id = parkingSlot.id;
        }
        slots.push(editedParkingSlot);
      }
    });
  });
  const completelyReplace =
    isMeetingRoom || original.slots.length !== slots.length;
  const organizerId =
    isMeetingRoom && data.organizer?.value ? data.organizer?.value : users[0];
  const scheduleRequest: ScheduleRequestUpdate = {
    schedule_slots: slots,
    title: data.title,
    description: data.description,
    visibility: data.visibility,
    ...getUserOrganizerIdentifier(organizerId)
  };

  if (editRecurring && data.recurring && original?.recurring_task?.id) {
    scheduleRequest.recurring = recurringStringToSaveData(
      data.recurring,
      original.recurring_task.start.split("T")[0],
      startTimeForRecurring,
      endTimeForRecurring
    );
    scheduleRequest.recurring.recurring_task_id = original.recurring_task.id;
  }

  const onlineMeeting = resolveOnlineMeetingUpdate(
    data.online_meeting,
    original.online_meeting?.provider ?? null
  );
  if (onlineMeeting !== undefined) {
    scheduleRequest.online_meeting = onlineMeeting;
  }

  try {
    completelyReplace
      ? await api.client.schedules.put(
          editRecurring && original.recurring_schedule_id
            ? original.recurring_schedule_id
            : original.id,
          scheduleRequest,
          undefined,
          orgSlug
        )
      : await api.client.schedules.patch(
          editRecurring && original.recurring_schedule_id
            ? original.recurring_schedule_id
            : original.id,
          scheduleRequest,
          undefined,
          orgSlug
        );
  } catch (e) {
    if (e instanceof ApiClientError) {
      const whenError = parseValidation(
        e.response.result,
        dayPart,
        true,
        "edit",
        dateFormat
      );
      return {
        success: false,
        message: whenError.error,
        ...whenError
      };
    }
    return {
      success: false,
      hasError: true,
      message: "Unknown error"
    };
  }
  return {
    success: true,
    hasError: false,
    message: ""
  };
}

const onlineMeetingErrorCodes = [
  "online_meeting_cannot_delete",
  "online_meeting_invalid_provider"
];
const isOnlineMeetingError = (code: string) => {
  return onlineMeetingErrorCodes.includes(code);
};

function parseValidation(
  validation:
    | Id
    | {
        context:
          | { daysOpen: number[] }
          | { dates: { [day: string]: string[] } };
      }
    | { errors: { slots: string; schedule_slots: string } }
    | { error: { code: string; message: string } }
    | { availability_errors: AvailabilityError[] }
    | null,
  dayPart: string,
  hasError: boolean,
  type: "edit" | "create",
  dateFormat: string
) {
  const hasContext =
    validation && "context" in validation && validation.context;
  const context = hasContext ? validation.context : null;
  const dates = context && "dates" in context ? context.dates : {};
  const datesWithError = Object.keys(dates).filter((x) => dates[x].length > 0);
  const datesFormatted = datesWithError.map((x) =>
    format(parseDateOnlyString(x), dateFormat)
  );
  const formattedDayPart = format(parseDateOnlyString(dayPart), dateFormat);
  if (validation && "availability_errors" in validation) {
    return {
      message: getAvailabilityErrorsMessage(
        validation.availability_errors,
        dateFormat
      ),
      isAvailabilityError: true,
      hasError,
      start: formattedDayPart
    };
  }

  if (
    validation &&
    "error" in validation &&
    isOnlineMeetingError(validation.error?.code) &&
    validation.error?.message
  ) {
    return {
      error: validation.error?.message,
      hasError
    };
  }

  if (
    validation &&
    "error" in validation &&
    validation.error?.code &&
    reservationErrorMessagesByCode[validation.error.code]
  ) {
    return {
      error: reservationErrorMessagesByCode[validation.error.code],
      hasError,
      start: formattedDayPart
    };
  }

  if (datesWithError.length > 0) {
    return {
      error:
        "We couldn't reserve in the following dates: " +
        datesFormatted.join(", "),
      hasError,
      start: datesFormatted[0]
    };
  }

  if (
    hasContext &&
    "daysOpen" in validation.context &&
    validation.context.daysOpen.length > 0
  ) {
    const days = [];
    for (const dayOfWeek of validation.context.daysOpen) {
      days.push(RESERVE_TEXTS.days[dayOfWeek]);
    }
    return {
      error:
        "The office is open only on the following days: " + days.join(", "),
      hasError,
      start: formattedDayPart
    };
  }

  if (
    validation &&
    "errors" in validation &&
    validation.errors &&
    validation.errors.slots &&
    validation.errors.slots === "range"
  ) {
    return {
      error: "There's no free capacity",
      hasError,
      start: formattedDayPart
    };
  }

  if (
    validation &&
    "errors" in validation &&
    validation.errors &&
    validation.errors.schedule_slots &&
    validation.errors.schedule_slots === "limit"
  ) {
    return {
      error:
        "You aren't allowed to reserve two desks at the same time. Please select a date & time when you do not have an existing reservation.",
      hasError
    };
  }

  return {
    error:
      type === "create"
        ? "There was an issue trying to reserve"
        : "There was an issue editing the reservation",
    hasError,
    start: formattedDayPart
  };
}

function recurringStringToSaveData(
  recurring: string,
  formattedStartDate: string,
  startTime: string,
  endTime: string
) {
  let rule: ReturnType<typeof rrulestr> | undefined;
  try {
    rule = rrulestr(recurring);
  } catch (error) {
    console.warn(
      "Failed to parse RRULE in recurringStringToSaveData:",
      recurring,
      error
    );
    // Fallback to a basic daily rule
    rule = new RRule({
      freq: RRule.DAILY,
      interval: 1
    });
  }
  const recurringWithoutDates = recurring
    .replaceAll(/DTSTART:(\d+)T(\d+)Z?/g, "")
    .replaceAll(/UNTIL=(\d+)T(\d+)Z?;?/g, "")
    .replaceAll(/RRULE:/g, "")
    .trim()
    .replaceAll(/\;$/g, "");
  const saveObject: {
    rrules: string[];
    start?: string;
    end?: string;
    recurring_task_id?: string;
  } = {
    rrules: [recurringWithoutDates]
  };

  saveObject.start = `${formattedStartDate}T${startTime}`;

  if (rule.options.until) {
    saveObject.end = `${format(rule.options.until, "yyyy-MM-dd")}T${endTime}`;
  }
  return saveObject;
}

export function recurringToText(schedule: ISchedule): string {
  if (!schedule.recurring_task) {
    return "";
  }
  const rrule = recurringToRRule(schedule);

  if (!rrule) {
    return "";
  }

  return rrule.toText();
}

function getUserSlotIdentifier(userIdOrEmail?: string | null) {
  const validEmail = isValidEmail(userIdOrEmail);
  return {
    user_id: validEmail ? null : (userIdOrEmail ?? null),
    email: validEmail ? userIdOrEmail : null
  };
}

function getUserOrganizerIdentifier(userIdOrEmail?: string | null) {
  const validEmail = isValidEmail(userIdOrEmail);
  return {
    organizer_id: validEmail ? null : (userIdOrEmail ?? null),
    organizer_email: validEmail ? userIdOrEmail : null
  };
}

export function recurringToRRule(schedule: ISchedule) {
  if (!schedule.recurring_task) {
    return "";
  }
  // rrule needs all dates in UTC
  const readDate = (date: string) =>
    convertTZ(parseISO(date.replace("Z", "")), "UTC");
  const options = {
    dtstart: schedule.recurring_task.start
      ? readDate(schedule.recurring_task.start)
      : undefined
  };
  if (!schedule.recurring_task.start) {
    // biome-ignore lint/performance/noDelete: <explanation>
    delete options.dtstart;
  }
  let strRules =
    schedule.recurring_task.rrules?.join("") ?? schedule.recurring_task.rrule;
  // Handle the special case where end is "0001-01-01T00:00:00Z" which represents "never ending"
  if (
    schedule.recurring_task.end &&
    schedule.recurring_task.end !== "0001-01-01T00:00:00Z"
  ) {
    strRules += `;UNTIL=${format(readDate(schedule.recurring_task.end), "yyyyMMdd'T'HHmmss")}`;
  }
  try {
    const rrule = rrulestr(strRules, options);
    return rrule;
  } catch (error) {
    console.warn("Failed to parse RRULE in recurringToRRule:", strRules, error);
    // Fallback to a basic daily rule
    return new RRule({
      freq: RRule.DAILY,
      interval: 1,
      ...options
    });
  }
}

export async function saveReservations(
  data: SaveReservationData,
  options: {
    isMeetingRoom: boolean;
    officeId: string;
    allowOthers: boolean;
    authUserId: string;
    organizerId: string | null;
    timeZone: string;
    office?: Office;
    noParkingResource?: Resource;
    dateFormat: string;
    orgSlug: string;
    /** Extra meeting rooms held by the same reservation, on top of `data.resource`. */
    rooms?: IResource[];
  }
): Promise<{
  hasError: boolean;
  message?: string;
  success: number;
  scheduleIds?: string[];
}> {
  validateReservationsData(data);
  const { resource } = data;
  if (!resource) {
    throw new Error("Undefined resource id");
  }

  const users: string[] = [];
  let organizerId: null | string = null;
  if (options.isMeetingRoom) {
    users.push(...data.users.map((x) => x.value));
    if (!options.allowOthers) {
      // when is not on behalf we add the current user
      if (!users.includes(options.authUserId)) {
        users.push(options.authUserId);
      }
    } else if (options.organizerId) {
      organizerId = options.organizerId;
      if (!users.includes(organizerId)) {
        users.push(organizerId);
      }
    }
  } else {
    // For desks, we want only One user
    const userForDesk = options.allowOthers
      ? data.users.length
        ? data.users[0].value
        : null
      : options.authUserId;
    organizerId = options.allowOthers ? userForDesk : null;
    if (userForDesk) {
      users.push(userForDesk);
    }
  }

  if (users.length <= 0) {
    return {
      hasError: true,
      message: "You need to select at least one guest or organizer",
      success: 0
    };
  }
  let firstStartDateForRecurring = "";
  let startTimeForRecurring = "";
  let endTimeForRecurring = "";
  const promises = data.weekDates.map(async (startDate) => {
    startDate.setHours(data.time.getHours());
    startDate.setMinutes(data.time.getMinutes());
    startDate.setSeconds(0);
    const dayPart = format(startDate, "yyyy-MM-dd");
    const endDate = addMinutes(startDate, data.duration.value);
    const startFormatted = `${dayPart}T${format(startDate, "HH:mm:ss")}.000Z`;
    if (!firstStartDateForRecurring) {
      firstStartDateForRecurring = dayPart;
      endTimeForRecurring = `${format(endDate, "HH:mm:ss")}.000Z`;
      startTimeForRecurring = `${format(startDate, "HH:mm:ss")}.000Z`;
    }

    const period: Period = {
      start: startFormatted,
      end: `${format(endDate, "yyyy-MM-dd")}T${format(endDate, "HH:mm:ss")}.000Z`,
      time_zone: options.timeZone
    };

    let scheduleRequest: ScheduleRequest & {
      time_zone?: string;
    } = {
      schedule_slots: []
    };
    if (options.allowOthers && organizerId) {
      scheduleRequest = {
        ...scheduleRequest,
        ...getUserOrganizerIdentifier(organizerId)
      };
    }

    const { title, description, visibility } = data;

    if (title) scheduleRequest.title = title;
    if (description) scheduleRequest.description = description;
    if (visibility) scheduleRequest.visibility = visibility;

    // Every user of a reservation must hold every one of its resources, so a
    // multi-room booking is the cartesian product of its rooms and its attendees.
    const resourceIds = [
      resource.id,
      ...(options.rooms ?? []).map((room) => room.id)
    ];

    scheduleRequest.schedule_slots.push(
      ...buildRoomReservationSlots({
        roomIds: resourceIds,
        users,
        period,
        toSlotIdentifier: getUserSlotIdentifier
      })
    );

    const teams = data.teams
      ? data.teams.map((currentTeam: { value: string }) => currentTeam.value)
      : [];

    teams.forEach((teamId) => {
      resourceIds.forEach((resourceId) => {
        scheduleRequest.schedule_slots.push({
          resource_id: resourceId,
          user_id: null,
          team_id: teamId,
          slots: 1,
          start: period.start,
          end: period.end,
          time_zone: period.time_zone
        });
      });
    });

    const parkingResourceId =
      options.noParkingResource &&
      data.parking_resource &&
      data.parking_resource &&
      data.parking_resource.id !== options.noParkingResource.id
        ? data.parking_resource.id
        : null;

    if (parkingResourceId !== null && users.length > 0) {
      scheduleRequest.schedule_slots.push({
        resource_id: parkingResourceId,
        ...getUserSlotIdentifier(users[0]),
        team_id: null,
        slots: 1,
        start: period.start,
        end: period.end,
        time_zone: period.time_zone
      });
    }

    if (data.recurring && data.recurring !== "no") {
      scheduleRequest.recurring = recurringStringToSaveData(
        data.recurring,
        firstStartDateForRecurring,
        startTimeForRecurring,
        endTimeForRecurring
      );
    }

    if (data.online_meeting?.provider) {
      // Only allow editing for Google Meet
      scheduleRequest.online_meeting = {
        provider: data.online_meeting.provider
      };
    } else if (data.online_meeting?.provider === null) {
      // User has explicitly turned off online meeting
      scheduleRequest.online_meeting = null;
    }

    try {
      const result = await api.client.schedules.create(
        options.officeId,
        {
          ...scheduleRequest
        },
        undefined,
        options.orgSlug
      );

      // Extract the schedule ID from the response
      let scheduleId: string | undefined;
      if (result.result && "id" in result.result) {
        scheduleId = result.result.id;
      }

      return {
        ...parseValidation(null, dayPart, false, "create", options.dateFormat),
        scheduleId
      };
    } catch (e) {
      if (e instanceof ApiClientError) {
        return parseValidation(
          e.response?.result ?? null,
          dayPart,
          true,
          "create",
          options.dateFormat
        );
      }
      return parseValidation(null, dayPart, true, "create", options.dateFormat);
    }
  });

  const results = (await Promise.all(promises)) as Array<
    | {
        hasError: false;
        scheduleId?: string;
        message?: string;
        start?: string;
        error?: undefined;
        isAvailabilityError?: boolean;
      }
    | {
        hasError: true;
        error: string;
        start?: string;
        isAvailabilityError?: boolean;
        message?: string;
        scheduleId?: undefined;
      }
  >;

  // Type the results array to include scheduleId for successful results
  const success = results.filter((x) => !x.hasError);
  const errors = results.filter((x) => x.hasError);
  const hasError = errors.length > 0;
  const grouping: { [idx: string]: string[] } = {};

  // If the first error is an availability error, we consider all errors as availability errors
  if (errors?.[0]?.isAvailabilityError) {
    const messages = errors.map((x) => x.message).join(" ");
    return {
      success: success.length,
      hasError: true,
      message: messages
    };
  }

  errors.forEach((err) => {
    grouping[err.error] = grouping[err.error] || [];
    if (err.start) grouping[err.error].push(err.start);
  });

  let message =
    !hasError && success.length === 1
      ? "Your reservation was made"
      : "All your reservations were made";
  if (errors.length > 0) {
    message = `We couldn't complete all your reservations:` + "\n";
    for (const error of Object.keys(grouping)) {
      message +=
        `${error} ${
          grouping[error].length > 0
            ? grouping[error].length === 1
              ? "for day"
              : "for days"
            : ""
        } ${grouping[error].join(", ")}` + "\n";
    }
  }
  // Collect all successful schedule IDs
  const scheduleIds = results
    .filter((x) => !x.hasError && x.scheduleId)
    .map((x) => x.scheduleId!)
    .filter(Boolean);

  return {
    success: success.length,
    hasError,
    message,
    scheduleIds: scheduleIds.length > 0 ? scheduleIds : undefined
  };
}

type AvailabilityError = {
  schedule_slot: {
    start: string;
    end: string;
    resource_id: string;
    recurring?:
      | {
          startDate: string;
          rrule: string;
        }
      // biome-ignore lint/complexity/noBannedTypes: <explanation>
      | {};
  };
  reason_code:
    | "overlaps_with_schedule"
    | "overlaps_with_recurring_task"
    | "office_future_limit"
    | "invalid_resource"
    | "resource_unavailable"
    | "office_not_available"
    | "resource_over_capacity"
    | "concurrent_not_allowed"
    | "concurrent_not_allowed_cross_office"
    | "resource_assignment_not_allowed"
    | "buffer_overlaps_with_schedule";
  reason: string;
  affected_dates: string[];
  details?: {
    /** Present when capacity was checked jointly across several rooms booked
     *  together — `total_slots` then carries their combined capacity. */
    group_resource_ids?: string[];
    total_slots?: number;
  };
};

/**
 * Tells you whether the user should be restricted from making reservations based on a Tactic Health
 * user status. The only time a user should be restricted is if the organization has health_setting's turned
 * on in that office & the user's health status is anything besides clear (with or without an end date)
 *
 * @param authenticatedUser
 * @returns boolean
 */
export function getOfficeHealthReservationRestrictions(
  authenticatedUser: AuthenticatedUser,
  office?: Office
) {
  return !!(
    office &&
    office.health_setting !== null &&
    authenticatedUser.user?.organization_user.health?.status !== "clear"
  );
}

export function getReserveCardKey(schedule: ISchedule) {
  return `res-${schedule.id}${schedule.slots.map((x) => x.id).join("-")}`;
}

const renderDatesText = (dates: string[], dateFormat: string) => {
  return `${dates.map((x) => format(parseDateOnlyString(x), dateFormat)).join(", ")}`;
};

export const availabilityErrorsMessagesByCode: Record<
  AvailabilityError["reason_code"],
  (error: AvailabilityError, dateFormat: string) => string
> = {
  overlaps_with_schedule: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` for ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation due to the overlap with the existing schedule${affectedDatesText}.`;
  },
  buffer_overlaps_with_schedule: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` for ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation, the resource is not available the buffer time${affectedDatesText}.`;
  },
  overlaps_with_recurring_task: (
    error: AvailabilityError,
    dateFormat: string
  ) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` for ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation due to the overlap with the existing recurring schedule${affectedDatesText}.`;
  },
  office_future_limit: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation${affectedDatesText}, because it is too far in the future.`;
  },
  invalid_resource: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation on this resource${affectedDatesText}.`;
  },
  resource_unavailable: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation${affectedDatesText}, because the resource is not available.`;
  },
  office_not_available: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation${affectedDatesText}, because the office is not available.`;
  },
  resource_over_capacity: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates, details } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    // Rooms booked together share one capacity check, so the failure is about the
    // group as a whole rather than any single room.
    return (
      getRoomsOverCapacityMessage({
        groupResourceIds: details?.group_resource_ids,
        totalSlots: details?.total_slots,
        affectedDatesText
      }) ??
      `Could not complete the reservation${affectedDatesText}, because the resource is over capacity.`
    );
  },
  concurrent_not_allowed: (error: AvailabilityError, dateFormat: string) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation because concurrent reservations are not allowed${affectedDatesText}.`;
  },
  resource_assignment_not_allowed: (
    error: AvailabilityError,
    dateFormat: string
  ) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation because resource assignment is not allowed${affectedDatesText}.`;
  },
  concurrent_not_allowed_cross_office: (
    error: AvailabilityError,
    dateFormat: string
  ) => {
    const { affected_dates } = error;
    const affectedDatesText = affected_dates?.length
      ? ` on ${renderDatesText(affected_dates, dateFormat)}`
      : "";
    return `Could not complete the reservation because concurrent reservations are not allowed across offices${affectedDatesText}.`;
  }
};

export function getAvailabilityErrorsMessage(
  availability_errors: AvailabilityError[],
  dateFormat = "MM/dd/yyyy"
) {
  // Remove the period from all but the last message
  return availability_errors
    .map((x, index) => {
      const message = availabilityErrorsMessagesByCode[x.reason_code]?.(
        x,
        dateFormat
      );
      // If this is not the last message, remove the trailing period
      return index < availability_errors.length - 1
        ? message.replace(/\.$/, "")
        : message;
    })
    .join(". ");
}
